import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { queryClient, TypedMutationConfig } from '@/lib/react-query';
import { ApiResponse, AxiosErrorCustom, axiosInstance } from '@/lib/axios';
// import { TChannelIntegration } from '../../_shared/types/channel-integration.type';
import { TGetListGooglePlayStoreChannelResponse } from './get-list-google-play-store-channel.api';
import { GET_LIST_GOOGLE_PLAY_STORE_CHANNEL_QUERY_KEY } from './get-list-google-play-store-channel.api';
import { TChannel } from '../../_shared/types/channel.type';

/**
 * Delete Integration Google Review Input Schema
 */
export const deleteGooglePlayStoreChannelValidationSchema = z.object({
  organizationChannelId: z.string({ required_error: 'Organization channel id is required' }),
});
export type TDeleteGooglePlayStoreChannelValidation = z.infer<typeof deleteGooglePlayStoreChannelValidationSchema>;
export type TDeleteGooglePlayStoreChannelResponse = ApiResponse<TChannel>;

export const deleteGooglePlayStoreChannel = async (
  payload: TDeleteGooglePlayStoreChannelValidation
): Promise<TDeleteGooglePlayStoreChannelResponse> => {
  const url = `/integration/google-playstore/org-channel/${payload.organizationChannelId}`;

  return axiosInstance.delete(url);
};

/**
 * Use Delete Google Review Integration React-Query Hook
 */
type UseDeleteGooglePlayStoreChannelOptions = {
  mutationConfig?: TypedMutationConfig<typeof deleteGooglePlayStoreChannel, AxiosErrorCustom>;
};

export const useDeleteGooglePlayStoreChannel = ({ mutationConfig }: UseDeleteGooglePlayStoreChannelOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: deleteGooglePlayStoreChannel,
    ...restConfig,
  });
};

/**
 * Use Delete Google Review Integration Optimistically React-Query Hook
 */
type UseDeleteGooglePlayStoreChannelOptimisticallyOptions = {
  mutationConfig?: TypedMutationConfig<typeof deleteGooglePlayStoreChannel, AxiosErrorCustom>;
};

type TDeleteGooglePlayStoreChannelOptimisticallyContext = {
  previousData: TGetListGooglePlayStoreChannelResponse | undefined;
};

export const useDeleteGooglePlayStoreChannelOptimistically = ({
  mutationConfig,
}: UseDeleteGooglePlayStoreChannelOptimisticallyOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: deleteGooglePlayStoreChannel,
    onMutate: async (data: TDeleteGooglePlayStoreChannelValidation) => {
      await queryClient.cancelQueries({ queryKey: [GET_LIST_GOOGLE_PLAY_STORE_CHANNEL_QUERY_KEY] });

      const previousData = queryClient.getQueryData<TGetListGooglePlayStoreChannelResponse>([
        GET_LIST_GOOGLE_PLAY_STORE_CHANNEL_QUERY_KEY,
      ]);

      queryClient.setQueriesData<TGetListGooglePlayStoreChannelResponse>(
        { queryKey: [GET_LIST_GOOGLE_PLAY_STORE_CHANNEL_QUERY_KEY] },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            payload: {
              ...oldData.payload,
              rows: oldData.payload.rows.filter(
                (channel) => channel.organizationChannelId !== data.organizationChannelId
              ),
            },
          };
        }
      );

      return { previousData } as TDeleteGooglePlayStoreChannelOptimisticallyContext;
    },

    onError: (_err, _data, context) => {
      if ((context as TDeleteGooglePlayStoreChannelOptimisticallyContext)?.previousData !== undefined) {
        queryClient.setQueryData(
          [GET_LIST_GOOGLE_PLAY_STORE_CHANNEL_QUERY_KEY],
          (context as TDeleteGooglePlayStoreChannelOptimisticallyContext).previousData
        );
      }
    },

    ...restConfig,
  });
};
